import React from 'react';
import { Skeleton, Row, Col, Card, Icon } from 'antd';
import { useI18n } from '../../hooks/useI18n';
import { messages } from '../../messages';
import { ApiErrorDetails } from '../apiErrorDetails/apiErrorDetails';
import { ApiError } from '../../hooks/useApi';
import styles from './groupedOrdersSummary.module.scss';
import { ArticleRows } from './articleRows';
import { GroupedOrdersSummary } from '../../types';
import { formatNumber } from '../../helpers/formatNumber/formatNumber';
import { useExpandedRows } from '../../hooks/useExpandedRows';

export interface GroupedOrdersSummaryProps {
  currencyCode: string;
  currencyPriceDecimalPlaces: number;
  resource: {
    isLoading: boolean;
    data: GroupedOrdersSummary | undefined;
    error: ApiError | undefined;
  };
}

export function SharedGroupedOrdersSummary({
  currencyCode,
  currencyPriceDecimalPlaces,
  resource,
}: GroupedOrdersSummaryProps) {
  const { t } = useI18n();
  const { expandedRowIds, handleExpand } = useExpandedRows<string>();

  return (
    <>
      <Row gutter={15}>
        <Col span={24}>
          <Card title={t(messages.article.articlesSummary)}>
            {resource.error && <ApiErrorDetails error={resource.error} />}
            {!resource.data && !resource.error && (
              <Skeleton paragraph={{ rows: 15 }} active={true} />
            )}
            {resource.data && !resource.error && (
              <Row
                style={{
                  background: '#fafafa',
                  padding: '12px 8px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                }}
              >
                <Col span={8}>{t(messages.article.article)}</Col>
                <Col span={2}>{t(messages.shared.quantity)}</Col>
                <Col span={2}>{t(messages.order.delivered)}</Col>
                <Col span={2}>{t(messages.order.deliveryDates)}</Col>
                <Col span={2}>{t(messages.shared.batchNumber)}</Col>
                <Col
                  span={2}
                >{`${t(messages.shared.price)} (${currencyCode})`}</Col>
                <Col span={2}>{t(messages.shared.totalPrice)}</Col>
                <Col span={2}>{t(messages.order.row)}</Col>
                <Col span={2}>{t(messages.order.orderNumber)}</Col>
              </Row>
            )}
            {resource.data?.articles.map((article) => {
              const allPricesEqual = article.rows.every(
                (r) => r.unitPrice === article.rows[0].unitPrice,
              );
              return (
                <Row
                  key={article.articleNumber}
                  className={styles.articleRow}
                  role="button"
                  aria-expanded={expandedRowIds.includes(article.articleNumber)}
                  onClick={() => handleExpand(article.articleNumber)}
                >
                  <Col span={8}>
                    <div className={styles.articleNumber}>
                      {article.articleNumber}
                    </div>
                    <div className={styles.articleDescription}>
                      {article.articleDescription}
                    </div>
                    <div className={styles.articleDescription}>
                      {article.articleColor}
                    </div>
                    <div className={styles.articleDescription}>
                      {article.articleSize}
                    </div>
                  </Col>
                  <Col span={16}>
                    <Row className={styles.firstOrderRow}>
                      <Col span={3}>
                        <div>
                          {article.rows.length > 1 && (
                            <Icon
                              style={{
                                transform: expandedRowIds.includes(
                                  article.articleNumber,
                                )
                                  ? 'rotate(90deg)'
                                  : 'rotate(0deg)',
                                transition: 'transform 0.24s',
                              }}
                              type="right"
                            />
                          )}
                          <span style={{ marginLeft: '10px' }}>
                            {article.totalQuantityOrdered}
                          </span>
                        </div>
                      </Col>
                      <Col span={3}>{article.totalQuantityDelivered}</Col>
                      <Col span={3}>
                        {article.rows.length === 1
                          ? article.rows[0].deliveryDates.map((date, i) => (
                              <div key={i}>{date}</div>
                            ))
                          : ''}
                      </Col>
                      <Col span={3}>
                        {article.rows.length === 1
                          ? article.rows[0].batchNo
                          : ''}
                      </Col>
                      <Col span={3}>
                        {allPricesEqual
                          ? formatNumber(
                              article.rows[0].unitPrice,
                              currencyPriceDecimalPlaces,
                            )
                          : ''}
                      </Col>
                      <Col span={3}>{article.totalPrice}</Col>
                      <Col span={3}>
                        {article.rows.length === 1
                          ? article.rows[0].rowNumber
                          : ''}
                      </Col>
                      <Col span={3}>
                        {article.rows.length === 1
                          ? article.rows[0].orderNumber
                          : ''}
                      </Col>
                    </Row>
                    {article.rows.length > 1 &&
                      expandedRowIds.includes(article.articleNumber) && (
                        <ArticleRows
                          rows={article.rows}
                          currencyPriceDecimalPlaces={
                            currencyPriceDecimalPlaces
                          }
                          hideUnitPrice={allPricesEqual}
                        />
                      )}
                  </Col>
                </Row>
              );
            })}
          </Card>
        </Col>
      </Row>
    </>
  );
}
